import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../form-components/InputComponent";
import FormSelect from "../form-components/SelectComponent";
import Swal from "sweetalert2";
import { Icon } from "../../components/Component";
import usersApis from "../../api/master/users";
import FormDatePicker from "../form-components/DatePicker";
import sequenceApis from "../../api/master/sequence";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
interface SelectOptions {
  label: string;
  value: string;
}
interface SequenceMasterProps {
  id?: string | null;
  formAction?: string | null;
  activeModules?: any;
  setAction?: () => void;
  toggleModelPopUp: () => void;
  projectSiteId?: any;
  projectSiteName?: string;
  projectId?: string;
}

const SequenceMaster: FC<SequenceMasterProps> = ({
  id,
  formAction,
  toggleModelPopUp,
  activeModules,
  projectSiteId,
  projectSiteName,
  projectId,
}) => {
  console.log("projectId", projectId, id);

  const [modules, setModules] = useState<SelectOptions[]>([]);
  // const [module, setModule] = useState<any>({});
  const [action, setAction] = useState(formAction);
  const methods = useForm({
    mode: "onChange",
  });

  const [originalData, setOriginalData] = useState<any[]>([]);
  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = methods;

  const onSubmit = async (data: any) => {
    delete data?.includeState;
    data.level = "Project";

    if (data.endDate) {
      data.endDate = dayjs(data.endDate).format("YYYY-MM-DD");
    }

    if (id) data.projectId = id;
    if (projectId) data.projectId = projectId;
    if (projectSiteId) data.siteId = projectSiteId;
    delete data?.moduleName;
    delete data?.siteName;
    try {
      if (!action && !id) {
        const response = await sequenceApis.createSequence(data);
        if (response?.status) {
          Swal.fire("Created!", "Sequence has been created", "success");
          toggleModelPopUp();
        } else {
          setAlertInfo({
            type: "error",
            message: response?.message || "Failed to create sequence",
            errors: [],
          });
        }
      } else if (action === "edit") {
        const response = await sequenceApis.updateSequenceById(id, {
          ...data,
        });
        if (response?.status) {
          Swal.fire("Updated!", "Sequence has been updated", "success");
          toggleModelPopUp();
        } else {
          setAlertInfo({
            type: "error",
            message: response?.message || "Failed to update sequence",
            errors: [],
          });
        }
      }
    } catch (error) {
      console.error("Error submitting sequence:", error);
      setAlertInfo({
        type: "error",
        message: "An error occurred while submitting the sequence",
        errors: [],
      });
    }
  };

  const fetchModuleConfig = async () => {
    try {
      const res = await usersApis.fetchSequenceModules({
        applicationAccess: "PMS",
      });
      const moduleConfigData = res.data;
      const filteredModuleConfigData = [...moduleConfigData].filter(
        (module: any) => !activeModules?.includes(Number(module?.moduleID))
      );

      setModules(
        filteredModuleConfigData.map((obj: any) => ({
          label: obj.moduleName,
          value: obj.moduleID.toString(),
        }))
      );
    } catch (err) {
      console.error("Error fetching module config:", err);
    }
  };

  const fetchModuleSequenceById = async (id: any) => {
    try {
      const res = await sequenceApis.fetchSequenceById(id);
      if (res?.status && res.data) {
        const { data } = res;
        // setModule({ name: data.moduleName, id: data.moduleId });
        setValue("moduleName", data.moduleName); // Add this line
        setValue("moduleId", data.moduleId.toString());
        setValue("sequenceNo", data.sequenceNo);
        setValue("endDate", dayjs(data.endDate));
        setValue("prefix", data.prefix);
        setValue("level", data.level);
        setValue("siteId", data.siteId);
        setValue("projectId", data.projectId);
      }
    } catch (error) {
      console.error("Error fetching sequence by ID:", error);
    }
  };

  useEffect(() => {
    fetchModuleConfig();
    // fetchAllSequences();
  }, []);

  // Keep Site field in sync with props (handles late or async projectSiteName)
  useEffect(() => {
    if (projectSiteId != null) {
      setValue("siteId", projectSiteId);
    }
    setValue("siteName", projectSiteName ?? "");
  }, [projectSiteId, projectSiteName, setValue]);

  useEffect(() => {
    if (id) {
      fetchModuleSequenceById(id);
    }
  }, [id]);

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? `Edit Sequence`
          : action === "view"
          ? "View Sequence"
          : "Create Sequence"}
      </h5>

      {alertInfo.message && (
        <div className="mb-3 mt-1">
          <Alert
            color={alertInfo.type === "error" ? "danger" : "success"}
            className="alert-icon"
          >
            <strong>{alertInfo.message}</strong>
            <Icon
              name={
                alertInfo.type === "error" ? "alert-circle" : "check-circle"
              }
            />
            {alertInfo.errors.length > 0 && (
              <ul>
                {alertInfo.errors.map((err: any, index: any) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            )}
          </Alert>
        </div>
      )}

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-md-6 mb-3">
              {action !== "edit" ? (
                <FormSelect
                  name="moduleId"
                  label="Module"
                  options={modules}
                  required={true}
                  disabled={action === "view"}
                />
              ) : (
                <FormInput
                  name="moduleName"
                  label="Module"
                  value={methods.getValues("moduleName")}
                  disabled={true}
                  type="text"
                />
              )}
            </div>
            <div className="col-md-6 mb-3">
              <FormInput
                name="prefix"
                label="PREFIX"
                type="text"
                maxLength={20}
                placeholder="Enter Prefix"
                required={true}
                disabled={action === "view"}
              />
            </div>

            <div className="col-md-6 mb-3">
              <FormInput
                name="sequenceNo"
                label="Sequence No"
                disabled={action === "view"}
                type="Number"
                placeholder="Enter Sequence Number"
                required={true}
              />
            </div>

            <div className="col-md-6 mb-3">
              {action === "view" ? (
                <FormInput
                  name="endDate"
                  label="End Date"
                  value={
                    methods.getValues("endDate")
                      ? dayjs(methods.getValues("endDate")).format("YYYY-MM-DD")
                      : ""
                  }
                  disabled={true}
                  type="text"
                />
              ) : (
                <FormDatePicker
                  disabled={action === "edit"}
                  name="endDate"
                  label="End Date"
                  minDate={new Date()}
                  required={true}
                />
              )}
            </div>

            <input type="hidden" name="level" value="Project" />
            <input type="hidden" name="projectId" value={projectId} />
            <input type="hidden" name="siteId" value={projectSiteId} />

            <div className="col-md-6 mb-3">
              <FormInput
                name="siteName"
                label="Site"
                value={projectSiteName || ""}
                disabled={true}
                type="text"
              />
            </div>
          </div>

          {action !== "view" ? (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="submit"
                      color="primary"
                    >
                      <span>{action === "edit" ? "Update" : "Submit"}</span>
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        toggleModelPopUp();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          ) : (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <div
                      className="btn btn-primary btn-md"
                      color="primary"
                      onClick={() => setAction("edit")}
                    >
                      <Icon name="pen" />
                      <span>Edit</span>
                    </div>
                  </li>
                </ul>
              </Col>
            </Row>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default SequenceMaster;
